import { useEffect, useMemo, useState } from "react";
import { GripVertical } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { moveLine, refuseCrossingOption, type OrderableLine } from "./lineOrder";

/**
 * Drag-to-reorder for the estimate and invoice line tables (#1441).
 *
 * Shared by both documents on purpose: one handle, one set of keys, one way a
 * failed save behaves. Two tables that reorder differently would be two things
 * for the office to learn, and the second one always gets the worse version.
 *
 * The order shown is LOCAL state that follows the server's copy. A drop moves
 * the row immediately and saves in the background — reordering is a visual
 * judgement, so waiting on a round trip to see the result makes it unusable —
 * and a rejected save puts the list back where it was and says why. Nothing is
 * ever left showing an order the server did not accept.
 *
 * It reorders and nothing else. It never blocks a delete, an edit or an add:
 * Corbin's condition on the whole feature was "i don't want it a hard rule to
 * where i cannot delete a part off of the estimate/invoice".
 */
export interface LineReorderOptions<T extends OrderableLine> {
    lines: T[];
    /** False on a locked document — no handles, no keys, no Tidy up. */
    enabled: boolean;
    /** Persist the whole order. Rejecting the promise reverts the move. */
    save: (lineIds: string[]) => Promise<unknown>;
}

export interface LineReorder<T extends OrderableLine> {
    /** The lines in the order to render — local while a save is in flight. */
    ordered: T[];
    /** Spread onto each `<tr>`; drives the drag itself. */
    rowProps: (row: T) => React.HTMLAttributes<HTMLTableRowElement> | undefined;
    /** The grab handle cell. Renders nothing when reordering is off. */
    renderHandle: (row: T) => React.ReactNode;
    /** Apply an order worked out elsewhere — what "Tidy up" calls. */
    apply: (lineIds: string[]) => void;
    saving: boolean;
}

export function useLineReorder<T extends OrderableLine>({
    lines,
    enabled,
    save,
}: LineReorderOptions<T>): LineReorder<T> {
    // `null` means "show exactly what the server sent". We only hold a local
    // order while one is in flight, so a refetch that brings someone else's
    // change is never fighting a stale copy of ours.
    const [pending, setPending] = useState<string[] | null>(null);
    const [dragId, setDragId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const serverIds = lines.map((l) => l.id).join(",");
    useEffect(() => {
        setPending(null);
    }, [serverIds]);

    const ordered = useMemo(() => {
        if (!pending) return lines;
        const byId = new Map(lines.map((l) => [l.id, l]));
        const out = pending.map((id) => byId.get(id)).filter((l): l is T => !!l);
        // A line that arrived while we were dragging must not vanish because our
        // pending order predates it.
        for (const line of lines) if (!pending.includes(line.id)) out.push(line);
        return out;
    }, [lines, pending]);

    function commit(next: T[]) {
        const before = ordered.map((l) => l.id);
        const nextIds = next.map((l) => l.id);
        if (nextIds.join(",") === before.join(",")) return;
        setPending(nextIds);
        setSaving(true);
        void save(nextIds)
            .catch((err: unknown) => {
                setPending(null); // back to the server's order, visibly.
                const message =
                    (err as { body?: { message?: string } })?.body?.message ??
                    (err as Error)?.message ??
                    "The new order could not be saved.";
                toast.error(message);
            })
            .finally(() => setSaving(false));
    }

    function moveTo(lineId: string, toIndex: number) {
        const refusal = refuseCrossingOption(ordered, lineId, toIndex);
        if (refusal) {
            toast.error(refusal);
            return;
        }
        commit(moveLine(ordered, lineId, toIndex));
    }

    function apply(lineIds: string[]) {
        const byId = new Map(ordered.map((l) => [l.id, l]));
        commit(lineIds.map((id) => byId.get(id)).filter((l): l is T => !!l));
    }

    const rowProps = (row: T): React.HTMLAttributes<HTMLTableRowElement> | undefined => {
        if (!enabled) return undefined;
        return {
            draggable: true,
            onDragStart: (e) => {
                setDragId(row.id);
                e.dataTransfer.effectAllowed = "move";
                // Firefox ignores a drag that carries no data.
                e.dataTransfer.setData("text/plain", row.id);
            },
            onDragOver: (e) => {
                if (!dragId || dragId === row.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setOverId(row.id);
            },
            onDragLeave: () => setOverId((cur) => (cur === row.id ? null : cur)),
            onDrop: (e) => {
                e.preventDefault();
                const id = dragId ?? e.dataTransfer.getData("text/plain");
                setDragId(null);
                setOverId(null);
                if (!id || id === row.id) return;
                moveTo(
                    id,
                    ordered.findIndex((l) => l.id === row.id),
                );
            },
            onDragEnd: () => {
                setDragId(null);
                setOverId(null);
            },
            className: cn(dragId === row.id && "opacity-50", overId === row.id && "border-t-2 border-t-primary"),
        };
    };

    const renderHandle = (row: T): React.ReactNode => {
        if (!enabled) return null;
        const index = ordered.findIndex((l) => l.id === row.id);
        return (
            <button
                type="button"
                // A real button, so it is reachable by Tab and the arrow keys
                // work — dragging inside a scrolling table on a phone in the
                // truck is miserable, and this is the same move without a mouse.
                className="cursor-grab rounded p-1 text-foreground/30 hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:cursor-grabbing"
                aria-label={`Move "${describe(row)}" — row ${index + 1} of ${ordered.length}. Use the up and down arrow keys.`}
                title="Drag to reorder, or use the up and down arrow keys"
                onKeyDown={(e) => {
                    if (e.key === "ArrowUp") {
                        e.preventDefault();
                        moveTo(row.id, index - 1);
                    } else if (e.key === "ArrowDown") {
                        e.preventDefault();
                        moveTo(row.id, index + 1);
                    }
                }}
            >
                <GripVertical className="h-3.5 w-3.5" />
            </button>
        );
    };

    return { ordered, rowProps, renderHandle, apply, saving };
}

/** What the screen reader says the row is. */
function describe(row: OrderableLine & { description?: string | null }): string {
    return (row.description ?? "").trim() || "this line";
}
